import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
    Box,
    LinearProgress,
    Paper
} from '@mui/material';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import CancelPresentationRoundedIcon from '@mui/icons-material/CancelPresentationRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PowerRoundedIcon from '@mui/icons-material/PowerRounded';
import { DataGrid } from '@mui/x-data-grid';

import BiMetric from '../../components/biMetric';
import MonthRangePicker from '../../components/monthRangePicker';
import MultiSelectFilter from '../../components/multiSelectFilter';
import PageHeader from '../../components/pageHeader';
import { PageIntro } from '../../components/ui';
import {
    formatBackendDatePtBr,
    formatMonthParam,
    getCurrentMonthStart,
    parseBackendDate
} from '../../utils/dateUtils';
import {
    formatCompactInteger,
    formatInteger,
    getNiceMax
} from '../../utils/numberUtils';
import {
    allReasonsOption,
    buildDistratoRows,
    buildDistratosSummary,
    buildReasonChartRows,
    buildReasonOptions,
    fetchDistratosMes,
    filterDistratosByReason
} from './distratosData';
import '../css/biAluguelAccess.css';

const minMonth = new Date(2025, 0, 1);

const formatChartReasonLabel = (value) => {
    const label = String(value);
    return label.length > 24 ? `${label.slice(0, 23)}...` : label;
};

const ReasonComboChart = ({ rows }) => {
    const [activeRow, setActiveRow] = useState(null);
    const visibleRows = rows.slice(0, 12);
    const maxUnits = getNiceMax(Math.max(...visibleRows.map((row) => row.units), 1));
    const maxContracted = getNiceMax(Math.max(...visibleRows.map((row) => row.contracted), 1));
    const width = 920;
    const height = 460;
    const padding = {
        top: 38,
        right: 92,
        bottom: 104,
        left: 72
    };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;
    const slotWidth = chartWidth / Math.max(visibleRows.length, 1);
    const groupWidth = Math.min(42, slotWidth * 0.62);
    const barWidth = Math.max(10, groupWidth / 2);
    const getUnitsY = (value) => padding.top + chartHeight - ((value / maxUnits) * chartHeight);
    const getContractedY = (value) => padding.top + chartHeight - ((value / maxContracted) * chartHeight);
    const points = visibleRows.map((row, index) => {
        const x = padding.left + (index * slotWidth) + (slotWidth / 2);
        const unitsY = getUnitsY(row.units);
        const contractedY = getContractedY(row.contracted);
        const groupX = x - groupWidth / 2;

        return {
            ...row,
            x,
            unitsY,
            contractedY,
            groupWidth,
            barWidth,
            unitsBarX: groupX,
            unitsBarY: unitsY,
            unitsBarHeight: padding.top + chartHeight - unitsY,
            contractedBarX: groupX + barWidth,
            contractedBarY: contractedY,
            contractedBarHeight: padding.top + chartHeight - contractedY
        };
    });
    const yTicks = [maxUnits, maxUnits / 2, 0];
    const contractedTicks = [maxContracted, maxContracted / 2, 0];
    const activePoint = activeRow
        ? points.find((point) => point.reason === activeRow.reason)
        : null;
    const activeTooltipStyle = activePoint
        ? {
            left: `${(activePoint.x / width) * 100}%`,
            top: `${(Math.min(activePoint.unitsY, activePoint.contractedY) / height) * 100}%`
        }
        : undefined;

    return (
        <Paper className='bi-aluguel-access-panel bi-ge-distratos-chart-panel'>
            <div className='bi-aluguel-access-panel-heading'>
                <div>
                    <p>Unidades por motivo</p>
                    <h2>Distratos por motivo</h2>
                </div>
                <BarChartRoundedIcon />
            </div>

            <div className='bi-ge-distratos-chart-legend' aria-hidden='true'>
                <span className='is-units'>Unidades</span>
                <span className='is-contracted'>Contratado kWh</span>
            </div>

            <div className='bi-ge-distratos-combo-chart' onMouseLeave={() => setActiveRow(null)}>
                <svg viewBox={`0 0 ${width} ${height}`} role='img' aria-label='Barras verticais de unidades distratadas e contratado por motivo'>
                    {yTicks.map((tick) => {
                        const y = getUnitsY(tick);

                        return (
                            <g key={tick}>
                                <line className='bi-ge-distratos-grid-line' x1={padding.left} x2={width - padding.right} y1={y} y2={y} />
                                <text className='bi-ge-distratos-axis-label' x={padding.left - 12} y={y + 5}>
                                    {formatInteger(Math.round(tick))}
                                </text>
                            </g>
                        );
                    })}
                    {contractedTicks.map((tick) => {
                        const y = getContractedY(tick);

                        return (
                            <text key={`contracted-${tick}`} className='bi-ge-distratos-axis-label is-contracted-axis' x={width - padding.right + 14} y={y + 5}>
                                {formatCompactInteger(tick)}
                            </text>
                        );
                    })}
                    <line className='bi-ge-distratos-axis' x1={padding.left} x2={padding.left} y1={padding.top} y2={height - padding.bottom} />
                    <line className='bi-ge-distratos-axis is-contracted-axis' x1={width - padding.right} x2={width - padding.right} y1={padding.top} y2={height - padding.bottom} />
                    <line className='bi-ge-distratos-axis' x1={padding.left} x2={width - padding.right} y1={height - padding.bottom} y2={height - padding.bottom} />
                    {points.map((point) => (
                        <g key={point.reason}>
                            <rect
                                className={`bi-ge-distratos-bar ${activeRow && activeRow.reason === point.reason ? 'is-active' : ''}`}
                                x={point.unitsBarX}
                                y={point.unitsBarY}
                                width={point.barWidth}
                                height={point.unitsBarHeight}
                                rx='7'
                            />
                            <rect
                                className={`bi-ge-distratos-bar is-contracted ${activeRow && activeRow.reason === point.reason ? 'is-active' : ''}`}
                                x={point.contractedBarX}
                                y={point.contractedBarY}
                                width={point.barWidth}
                                height={point.contractedBarHeight}
                                rx='7'
                            />
                            <rect
                                aria-label={`${point.reason}: ${formatInteger(point.units)} unidades`}
                                className='bi-ge-distratos-hit-area'
                                x={point.x - (slotWidth / 2)}
                                y={padding.top}
                                width={slotWidth}
                                height={chartHeight}
                                tabIndex='0'
                                onBlur={() => setActiveRow(null)}
                                onFocus={() => setActiveRow(point)}
                                onMouseEnter={() => setActiveRow(point)}
                            />
                            <text className='bi-ge-distratos-bar-value' x={point.unitsBarX + (point.barWidth / 2)} y={point.unitsBarY - 8}>
                                {formatInteger(point.units)}
                            </text>
                            <text className='bi-ge-distratos-y-label' x={point.x} y={height - padding.bottom + 22} transform={`rotate(-40 ${point.x} ${height - padding.bottom + 22})`}>
                                {formatChartReasonLabel(point.reason)}
                            </text>
                        </g>
                    ))}
                </svg>

                {activePoint && (
                    <div className='bi-ge-distratos-chart-tooltip' style={activeTooltipStyle}>
                        <span>{activePoint.reason}</span>
                        <strong>{formatInteger(activePoint.units)} unidades</strong>
                        <small>{formatInteger(activePoint.clients)} clientes</small>
                        <small>{formatInteger(activePoint.contracted)} kWh contratados</small>
                    </div>
                )}
            </div>
        </Paper>
    );
};

const emptyRowsLabel = 'Nenhum distrato encontrado para os filtros selecionados.';

const DistratosBIGE = () => {
    const [dataInicio, setDataInicio] = useState(getCurrentMonthStart);
    const [dataFim, setDataFim] = useState(getCurrentMonthStart);
    const [selectedReasons, setSelectedReasons] = useState([allReasonsOption]);
    const [distratos, setDistratos] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const loadDistratos = useCallback(async (startDate, endDate) => {
        setIsLoading(true);
        const monthDistratos = await fetchDistratosMes({
            dataInicio: formatMonthParam(startDate),
            dataFim: formatMonthParam(endDate)
        });
        setDistratos(monthDistratos);
        setIsLoading(false);
    }, []);

    useEffect(() => {
        loadDistratos(dataInicio, dataFim);
    }, [dataFim, dataInicio, loadDistratos]);

    const filteredDistratos = useMemo(
        () => filterDistratosByReason(distratos, selectedReasons),
        [distratos, selectedReasons]
    );
    const summary = useMemo(() => buildDistratosSummary(filteredDistratos), [filteredDistratos]);
    const reasonOptions = useMemo(() => buildReasonOptions(distratos), [distratos]);
    const chartRows = useMemo(() => buildReasonChartRows(filteredDistratos), [filteredDistratos]);
    const rows = useMemo(() => buildDistratoRows(filteredDistratos), [filteredDistratos]);

    const handleDateRangeApply = (startDate, endDate) => {
        setDataInicio(startDate);
        setDataFim(endDate);
        setSelectedReasons([allReasonsOption]);
    };

    const columns = useMemo(() => [
        {
            field: 'motivo',
            headerName: 'Motivo',
            minWidth: 220,
            flex: 1
        },
        {
            field: 'unidade',
            headerName: 'Unidade',
            minWidth: 170,
            flex: 0.7
        },
        {
            field: 'contratado',
            headerName: 'Contratado (kWh)',
            type: 'number',
            minWidth: 180,
            flex: 0.7,
            valueFormatter: (value) => formatInteger(value)
        },
        {
            field: 'inicio',
            headerName: 'Assinatura',
            minWidth: 150,
            flex: 0.65,
            valueGetter: (value) => parseBackendDate(value),
            valueFormatter: (value) => formatBackendDatePtBr(value)
        }
    ], []);

    return (
        <Box className='mainBox bi-aluguel-access-page bi-ge-indications-page bi-ge-distratos-page'>
            <PageHeader>BI Aluguel - Distratos</PageHeader>

            <PageIntro eyebrow='BI Aluguel' title='Distratos'>
                Distratos registrados no período, com motivo e valor envolvido.
            </PageIntro>

            <section className='bi-aluguel-access-shell bi-ge-indications-shell'>
                {isLoading && <LinearProgress className='bi-aluguel-access-loading' />}

                <div className='bi-ge-distratos-filter-row'>
                    <div className='bi-aluguel-access-filters bi-ge-indications-filters bi-ge-distratos-period-filter'>
                        <MonthRangePicker
                            startDate={dataInicio}
                            endDate={dataFim}
                            minDate={minMonth}
                            maxDate={getCurrentMonthStart()}
                            disabled={isLoading}
                            onApply={handleDateRangeApply}
                        />
                    </div>

                    <MultiSelectFilter
                        className='bi-ge-distratos-reason-filter'
                        labelId='bi-ge-distratos-reason-label'
                        label='Motivo'
                        value={selectedReasons}
                        options={reasonOptions}
                        disabled={isLoading}
                        allOptionValue={allReasonsOption}
                        onChange={setSelectedReasons}
                    />
                </div>

                <div className='bi-aluguel-access-metrics bi-ge-indications-metrics bi-ge-distratos-metrics'>
                    <BiMetric
                        icon={GroupsRoundedIcon}
                        label='Clientes distratados'
                        value={formatInteger(summary.totalClients)}
                        subtitle={`${formatInteger(summary.totalUnits)} unidades`}
                        tone='green'
                    />
                        <BiMetric
                            icon={PowerRoundedIcon}
                            label='Contratado kWh'
                            value={formatInteger(summary.totalContracted)}
                            subtitle='Soma do período'
                            tone='blue'
                        />
                    <BiMetric
                        icon={CancelPresentationRoundedIcon}
                        label='Distratos'
                        value={formatInteger(rows.length)}
                        subtitle='Registros na lista'
                    />
                </div>

                <ReasonComboChart rows={chartRows} />

                <Paper className='bi-aluguel-access-panel bi-aluguel-access-table-panel'>
                    <div className='bi-aluguel-access-panel-heading'>
                        <div>
                            <p>Distratos filtrados</p>
                            <h2>Lista por unidade</h2>
                        </div>
                        <AssessmentRoundedIcon />
                    </div>

                    <div className='bi-aluguel-access-grid-shell'>
                        <DataGrid
                            className='bi-aluguel-access-grid'
                            rows={rows}
                            columns={columns}
                            loading={isLoading}
                            disableRowSelectionOnClick
                            localeText={{ noRowsLabel: emptyRowsLabel }}
                            pageSizeOptions={[25, 50, 100]}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 50
                                    }
                                },
                                sorting: {
                                    sortModel: [{ field: 'contratado', sort: 'desc' }]
                                }
                            }}
                        />
                    </div>
                </Paper>
            </section>
        </Box>
    );
};

export default DistratosBIGE;
